/**
 * Ranking de retención entre clínicas (`/patients` tab Red).
 *
 * Fan-out de APIs por-clínica: no hay endpoint de red para recurrencia / no-volvieron.
 * Ver docs/PATIENTS_NETWORK_RANKING.md
 */

import type { PacientesNoVolvieronResumen, PacientesRecurrencia } from './metricsTypes';

/** Tope de clínicas en vuelo a la vez (2 requests cada una). */
export const PATIENTS_NETWORK_CONCURRENCY = 6;

export interface PatientsNetworkRow {
  tenantId: string;
  name: string;
  tasaRecurrencia: number | null;
  noReturnTotal: number | null;
  atendidos: number | null;
  /** no-retorno / atendidos × 100; null si no hay base. */
  noReturnRatePct: number | null;
}

export type PatientsNetworkSortKey = 'name' | 'tasa' | 'noReturn' | 'noReturnRate';

export function buildPatientsNetworkRow(input: {
  tenantId: string;
  name: string;
  recurrencia: PacientesRecurrencia | null;
  noVolvieron: PacientesNoVolvieronResumen | null;
}): PatientsNetworkRow {
  const atendidos = input.recurrencia?.atendidos ?? null;
  const noReturnTotal = input.noVolvieron?.total ?? null;
  const noReturnRatePct =
    atendidos != null && atendidos > 0 && noReturnTotal != null
      ? Number(((noReturnTotal / atendidos) * 100).toFixed(1))
      : null;

  return {
    tenantId: input.tenantId,
    name: input.name,
    tasaRecurrencia: input.recurrencia?.tasaRecurrencia ?? null,
    noReturnTotal,
    atendidos,
    noReturnRatePct,
  };
}

function compareNullableDesc(a: number | null, b: number | null): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return b - a;
}

function compareNullableAsc(a: number | null, b: number | null): number {
  return compareNullableDesc(b, a);
}

/** Default: peor primero (más no-retorno, empate menor tasa). */
export function sortPatientsNetworkRows(
  rows: PatientsNetworkRow[],
  sortKey: PatientsNetworkSortKey = 'noReturn',
  desc = true
): PatientsNetworkRow[] {
  const sign = desc ? 1 : -1;
  return [...rows].sort((a, b) => {
    let cmp = 0;
    if (sortKey === 'name') {
      cmp = a.name.localeCompare(b.name) * sign;
    } else if (sortKey === 'tasa') {
      cmp = compareNullableAsc(a.tasaRecurrencia, b.tasaRecurrencia) * sign;
    } else if (sortKey === 'noReturnRate') {
      cmp = compareNullableDesc(a.noReturnRatePct, b.noReturnRatePct) * sign;
    } else {
      cmp = compareNullableDesc(a.noReturnTotal, b.noReturnTotal) * sign;
    }
    if (cmp !== 0) return cmp;
    const tasaTie = compareNullableAsc(a.tasaRecurrencia, b.tasaRecurrencia);
    if (tasaTie !== 0) return tasaTie;
    return a.tenantId.localeCompare(b.tenantId);
  });
}

export function patientsNetworkExtremes(rows: PatientsNetworkRow[]): {
  worstTenantId: string | null;
  bestTenantId: string | null;
} {
  const ranked = sortPatientsNetworkRows(rows, 'noReturn', true);
  const usable = ranked.filter((row) => row.noReturnTotal != null || row.tasaRecurrencia != null);
  if (usable.length < 2) {
    return { worstTenantId: null, bestTenantId: null };
  }
  const worst = usable[0];
  const best = usable[usable.length - 1];
  if (worst.tenantId === best.tenantId) {
    return { worstTenantId: null, bestTenantId: null };
  }
  return { worstTenantId: worst.tenantId, bestTenantId: best.tenantId };
}

export async function mapPool<T, R>(items: T[], limit: number, mapper: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = [];
  for (let i = 0; i < items.length; i += Math.max(1, limit)) {
    const chunk = items.slice(i, i + Math.max(1, limit));
    results.push(...(await Promise.all(chunk.map(mapper))));
  }
  return results;
}
